import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Grid, 
  Container,
  alpha,
  Divider,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  TextField,
  Snackbar,
  Alert
} from "@mui/material";
import { Link } from "react-router-dom";
import Logo from "../img/logo_avg_vf1.png-removebg-preview.png";
import { Mail, MapPin, X as CloseIcon, ChevronRight, Facebook, Send, Phone, Instagram } from "lucide-react";
import { FaTiktok } from "react-icons/fa";
import { db } from "../firebase/config";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";

const Footer = () => {
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const subscribeNewsletter = async () => {
    const email = (newsletterEmail || "").trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setSnackbarMessage('Veuillez entrer une adresse email valide.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setNewsletterSubmitting(true);
    try {
      await setDoc(
        doc(db, 'newsletter_subscribers', email),
        {
          email,
          status: 'subscribed',
          source: 'footer',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      setNewsletterEmail('');
      setSnackbarMessage("Merci ! Votre inscription à la newsletter est confirmée.");
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage("Impossible de vous inscrire pour le moment. Veuillez réessayer.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#014527",
          color: "white",
          pt: 6,
          pb: 4,
          mt: 4,
          boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.5) 100%)",
            boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)",
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Informations de l'entreprise */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                textAlign: { xs: "center", sm: "left" },
                mb: 2
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: "bold", 
                    mb: 3,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    position: "relative",
                    display: "inline-block",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -8,
                      left: 0,
                      width: "40px",
                      height: "3px",
                      backgroundColor: "#fff",
                      borderRadius: "2px",
                    }
                  }}
                >
                  Agri Vision Gabon
                </Typography>
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: { xs: "center", sm: "flex-start" }, 
                    mb: 2 
                  }}
                >
                  <IconButton size="small" sx={{ color: "#fff", mr: 1, p: 0.5, bgcolor: alpha("#fff", 0.1) }}>
                    <Mail size={16} />
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        color: alpha("#fff", 0.8),
                        transform: "translateX(3px)"
                      }
                    }}
                  >
                    agrivision.ga@gmail.com
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: { xs: "center", sm: "flex-start" } 
                  }}
                >
                  <IconButton size="small" sx={{ color: "#fff", mr: 1, p: 0.5, bgcolor: alpha("#fff", 0.1) }}>
                    <MapPin size={16} />
                  </IconButton>
                  <Typography variant="body2">
                    Siège social : quartier Batavea, BP 4457
                  </Typography>
                </Box>
                
                {/* Réseaux sociaux */}
                <Box sx={{ mt: 3 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: "bold", 
                      mb: 2,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      textAlign: { xs: "center", sm: "left" }
                    }}
                  >
                    Suivez-nous
                  </Typography>
                  <Box 
                    sx={{ 
                      display: "flex", 
                      gap: 2,
                      justifyContent: { xs: "center", sm: "flex-start" }
                    }}
                  >
                    <IconButton
                      component="a"
                      href="https://www.facebook.com/profile.php?id=100064111301458"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#fff",
                        bgcolor: alpha("#1877F2", 0.2),
                        border: "1px solid",
                        borderColor: alpha("#1877F2", 0.3),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "#1877F2",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(24, 119, 242, 0.3)"
                        }
                      }}
                    >
                      <Facebook size={20} />
                    </IconButton>
                    <IconButton
                      component="a"
                      href="https://www.instagram.com/harvest_gabon?igsh=MWEwdmRrZDZ0a29sdg=="
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#fff",
                        bgcolor: alpha("#E1306C", 0.2),
                        border: "1px solid",
                        borderColor: alpha("#E1306C", 0.3),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "#E1306C",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(225, 48, 108, 0.3)"
                        }
                      }}
                    >
                      <Instagram size={20} />
                    </IconButton>
                    <IconButton
                      component="a"
                      href="https://www.tiktok.com/@agrivision.gabon?is_from_webapp=1&sender_device=pc"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#fff",
                        bgcolor: alpha("#000", 0.2),
                        border: "1px solid",
                        borderColor: alpha("#000", 0.3),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "#000",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                        }
                      }}
                    >
                      <FaTiktok size={20} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Liens rapides */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: "bold", 
                    mb: 3,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    position: "relative",
                    display: "inline-block",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -8,
                      left: 0,
                      width: "40px",
                      height: "3px",
                      backgroundColor: "#fff",
                      borderRadius: "2px",
                    }
                  }}
                >
                  Liens Rapides
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box 
                    component={Link} 
                    to="/" 
                    sx={{ 
                      color: "white", 
                      textDecoration: "none", 
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        color: alpha("#fff", 0.8),
                        transform: "translateX(5px)"
                      }
                    }}
                  >
                    <ChevronRight size={16} style={{ marginRight: "4px", opacity: 0.7 }} />
                    <Typography variant="body2">Accueil</Typography>
                  </Box>
                  <Box 
                    component={Link} 
                    to="/services" 
                    sx={{ 
                      color: "white", 
                      textDecoration: "none", 
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        color: alpha("#fff", 0.8),
                        transform: "translateX(5px)"
                      }
                    }}
                  >
                    <ChevronRight size={16} style={{ marginRight: "4px", opacity: 0.7 }} />
                    <Typography variant="body2">Services</Typography>
                  </Box>
                  <Box 
                    onClick={() => setContactDialogOpen(true)}
                    sx={{ 
                      color: "white", 
                      textDecoration: "none", 
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        color: alpha("#fff", 0.8),
                        transform: "translateX(5px)"
                      }
                    }}
                  >
                    <ChevronRight size={16} style={{ marginRight: "4px", opacity: 0.7 }} />
                    <Typography variant="body2">Contact</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Administration et politique */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: "bold", 
                    mb: 3,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    position: "relative",
                    display: "inline-block",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -8,
                      left: 0,
                      width: "40px",
                      height: "3px",
                      backgroundColor: "#fff",
                      borderRadius: "2px",
                    }
                  }}
                >
                  Administration
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box 
                  component={Link} 
                  to="/admin-login" 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{ 
                    color: "white", 
                    textDecoration: "none", 
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "center", sm: "flex-start" },
                    transition: "all 0.2s ease",
                    "&:hover": { 
                      color: alpha("#fff", 0.8),
                      transform: "translateX(5px)"
                    }
                  }}
                >
                  <ChevronRight size={16} style={{ marginRight: "4px", opacity: 0.7 }} />
                  <Typography variant="body2">Connexion Administration</Typography>
                </Box>
                  <Box 
                    sx={{ 
                      color: "white", 
                      textDecoration: "none", 
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        color: alpha("#fff", 0.8),
                        transform: "translateX(5px)"
                      }
                    }}
                    onClick={() => setPolicyDialogOpen(true)}
                  >
                    <ChevronRight size={16} style={{ marginRight: "4px", opacity: 0.7 }} />
                    <Typography variant="body2">Politique de confidentialité</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: { xs: 4, sm: 5 } }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 3,
                bgcolor: alpha("#000", 0.18),
                border: `1px solid ${alpha("#fff", 0.18)}`,
                backdropFilter: "blur(10px)",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 900, mb: 0.5 }}>
                    Newsletter
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha("#fff", 0.88), lineHeight: 1.7 }}>
                    Recevez nos actualités, conseils et offres AVG Consulting.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1.5,
                      justifyContent: { xs: "center", md: "flex-end" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <TextField
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      placeholder="Votre email"
                      size="small"
                      fullWidth
                      sx={{
                        maxWidth: { xs: "100%", sm: 320 },
                        "& .MuiOutlinedInput-root": {
                          bgcolor: alpha("#fff", 0.08),
                          color: "white",
                          borderRadius: 2,
                          "& fieldset": { borderColor: alpha("#fff", 0.25) },
                          "&:hover fieldset": { borderColor: alpha("#fff", 0.45) },
                          "&.Mui-focused fieldset": { borderColor: alpha("#fff", 0.6) },
                        },
                        "& input::placeholder": { color: alpha("#fff", 0.75) },
                      }}
                      inputProps={{ inputMode: "email" }}
                    />
                    <Button
                      variant="contained"
                      onClick={subscribeNewsletter}
                      disabled={newsletterSubmitting}
                      startIcon={<Send size={18} />}
                      sx={{
                        bgcolor: "#fff",
                        color: "#014527",
                        fontWeight: 900,
                        borderRadius: 2,
                        textTransform: "none",
                        px: 2.5,
                        py: 1.1,
                        "&:hover": { bgcolor: alpha("#fff", 0.9) },
                      }}
                    >
                      S’inscrire
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Copyright */}
          <Divider sx={{ 
            my: 4, 
            borderColor: alpha("#fff", 0.2),
            width: "100%"
          }}/>
          <Typography 
            variant="body2" 
            sx={{ 
              textAlign: "center", 
              color: alpha("#fff", 0.8),
              fontWeight: 300
            }}
          >
            &copy; {new Date().getFullYear()} Agri Vision Gabon. Tous droits réservés.
          </Typography>
        </Container>
      </Box>

      {/* Modal for Contact */}
      <Dialog 
        open={contactDialogOpen} 
        onClose={() => setContactDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 0,
          bgcolor: "linear-gradient(135deg, #014527 0%, #01331d 100%)",
          color: "white",
          position: "relative"
        }}>
          <Box sx={{ p: 3, display: "flex", alignItems: "center" }}>
            <img src={Logo} alt="Agri Vision Gabon Logo" style={{ height: "50px", marginRight: "16px" }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Contactez-nous
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Nous sommes là pour vous aider
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setContactDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              bgcolor: alpha("#fff", 0.1),
              "&:hover": {
                bgcolor: alpha("#fff", 0.2),
              }
            }}
          >
            <CloseIcon size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Grid container>
            {/* Informations de contact */}
            <Grid item xs={12} md={5} sx={{ 
              bgcolor: alpha("#014527", 0.05), 
              p: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3, color: "#014527" }}>
                Nos coordonnées
              </Typography>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                <IconButton size="small" sx={{ 
                  color: "#014527", 
                  bgcolor: alpha("#014527", 0.1),
                  mr: 2,
                  p: 1
                }}>
                  <Mail size={18} />
                </IconButton>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "600", color: "#014527" }}>
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    agrivision.ga@gmail.com
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2.5 }}>
                <IconButton size="small" sx={{ 
                  color: "#014527", 
                  bgcolor: alpha("#014527", 0.1),
                  mr: 2,
                  p: 1,
                  mt: 0.5
                }}>
                  <MapPin size={18} />
                </IconButton>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "600", color: "#014527" }}>
                    Siège social
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.5 }}>
                    Quartier Batavea, BP 4457<br />
                    Libreville, Gabon
                  </Typography>
                </Box>
              </Box>
              
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                bgcolor: alpha("#014527", 0.08), 
                borderLeft: "4px solid #014527",
                borderRadius: 2,
                mt: 2
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: "600", 
                  color: "#014527",
                  mb: 1
                }}>
                  💡 Conseil
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85rem" }}>
                  Pour une réponse plus rapide, précisez l'objet de votre demande dans le champ "Sujet".
                </Typography>
              </Paper>
            </Grid>
            
            {/* Formulaire de contact */}
            <Grid item xs={12} md={7} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3, color: "#014527" }}>
                Envoyez-nous un message
              </Typography>
              
              <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom complet"
                      variant="outlined"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#014527',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#014527',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#014527',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      variant="outlined"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#014527',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#014527',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#014527',
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                
                <TextField
                  fullWidth
                  label="Sujet"
                  variant="outlined"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#014527',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#014527',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#014527',
                    },
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#014527',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#014527',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#014527',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, bgcolor: alpha("#f5f5f5", 0.5) }}>
          <Button 
            onClick={() => setContactDialogOpen(false)}
            sx={{
              color: "#666",
              fontWeight: 500,
              px: 2.5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { 
                bgcolor: alpha("#666", 0.1)
              }
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={() => {
              const { name, email, subject, message } = contactForm;
              if (!name || !email || !subject || !message) {
                setSnackbarMessage('Veuillez remplir tous les champs');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
              }
              
              const mailtoLink = `mailto:agrivision.ga@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
                `Nom: ${name}\nEmail: ${email}\n\nMessage:\n${message}`
              )}`;
              
              window.location.href = mailtoLink;
              
              setContactForm({ name: '', email: '', subject: '', message: '' });
              setContactDialogOpen(false);
              setSnackbarMessage('Votre client email va s\'ouvrir pour envoyer le message');
              setSnackbarSeverity('success');
              setSnackbarOpen(true);
            }}
            variant="contained"
            startIcon={<Send size={18} />}
            sx={{
              bgcolor: "#014527",
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.95rem",
              boxShadow: "0 4px 12px rgba(1, 69, 39, 0.3)",
              "&:hover": { 
                bgcolor: "#01331d",
                boxShadow: "0 6px 16px rgba(1, 69, 39, 0.4)"
              }
            }}
          >
            Envoyer le message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for Privacy Policy */}
      <Dialog 
        open={policyDialogOpen} 
        onClose={() => setPolicyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3,
          bgcolor: "#014527",
          color: "white",
          position: "relative"
        }}>
          <Box display="flex" alignItems="center">
            <img src={Logo} alt="Agri Vision Gabon Logo" style={{ height: "40px", marginRight: "16px" }} />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Politique de Confidentialité
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setPolicyDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              bgcolor: alpha("#fff", 0.1),
              "&:hover": {
                bgcolor: alpha("#fff", 0.2),
              }
            }}
          >
            <CloseIcon size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 4 }}>
          <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#014527" }}>
              Introduction
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Agri Vision Gabon respecte votre vie privée et s'engage à protéger vos données personnelles conformément aux lois applicables, notamment :
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Règlement Général sur la Protection des Données (RGPD) pour les utilisateurs situés dans l'Union européenne.
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                La loi gabonaise n°001/2011 du 25 septembre 2011, modifiée par la loi n°025/2023 du 12 juillet 2023, relative à la protection des données à caractère personnel.
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Les normes et bonnes pratiques internationales.
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: "bold", color: "#014527" }}>
              Vos Droits
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Vous disposez des droits suivants concernant vos données personnelles :
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Droit d'accès, de modification et de suppression de vos données.
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Droit de portabilité des données.
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Droit de limitation du traitement.
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: "bold", color: "#014527" }}>
              Sécurité
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Nous utilisons des technologies avancées pour garantir la sécurité et la confidentialité de vos données.
            </Typography>
            
            <Paper elevation={0} sx={{ 
              p: 2, 
              bgcolor: alpha("#014527", 0.05), 
              borderLeft: "4px solid #014527",
              borderRadius: 1,
              mb: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Pour toute question, contactez-nous à agrivision.ga@gmail.com.
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setPolicyDialogOpen(false)} 
            variant="contained" 
            sx={{
              bgcolor: "#014527",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.9rem",
              "&:hover": { 
                bgcolor: "#01331d"
              }
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Footer;
